/**
 * ErrorBoundary — catches render errors in any child component tree.
 *
 * React error boundaries must be class components (no functional equivalent).
 * Wrap heavy subtrees (router outlet, individual pages) to prevent a single
 * component crash from taking down the entire app.
 *
 * Usage:
 *   // Wrap the router outlet
 *   <ErrorBoundary>
 *     <AppRouter ... />
 *   </ErrorBoundary>
 *
 *   // Wrap a single page with a custom fallback
 *   <ErrorBoundary fallback={<p>Could not load this section.</p>}>
 *     <HeavyComponent />
 *   </ErrorBoundary>
 *
 * In tests, use {@link withErrorBoundary} to assert that errors render the
 * fallback UI rather than propagating.
 */
import { Component, ErrorInfo, ReactNode } from 'react';
import { datadogRum } from '@datadog/browser-rum';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Custom fallback UI. Defaults to a centered error message with a reload
   * button. Receives the caught error for display if needed.
   */
  fallback?: ReactNode | ((error: Error) => ReactNode);
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Default fallback UI
// ---------------------------------------------------------------------------

function DefaultFallback({
  error,
  onReset,
}: {
  error: Error | null;
  onReset: () => void;
}) {
  return (
    <div
      role='alert'
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <h2>Something went wrong</h2>
      {error && (
        <p style={{ color: '#666', marginBottom: '1rem' }}>{error.message}</p>
      )}
      <button type='button' className='p-button p-component' onClick={onReset}>
        <span className='p-button-label'>Reload page</span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ErrorBoundary class component
// ---------------------------------------------------------------------------

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    datadogRum.addError(error, { componentStack: info.componentStack ?? '' });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (fallback !== undefined) {
        return typeof fallback === 'function' ? fallback(error!) : fallback;
      }
      return <DefaultFallback error={error} onReset={this.handleReset} />;
    }

    return children;
  }
}

/**
 * Convenience HOC — wraps a component in an ErrorBoundary.
 *
 * @example
 * const SafeCase = withErrorBoundary(Case);
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ErrorBoundaryProps['fallback'],
): React.FC<P> {
  const Wrapped: React.FC<P> = (props) => (
    <ErrorBoundary fallback={fallback}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );
  Wrapped.displayName = `withErrorBoundary(${WrappedComponent.displayName ?? WrappedComponent.name})`;
  return Wrapped;
}
